import React, { useState, useEffect } from 'react';
import { useWorkspace } from '../../../context/WorkspaceContext';
import { useAuth } from '../../../context/AuthContext';
import { Card } from '../../../design-system/Card';
import { Button } from '../../../design-system/Button';
import { Badge } from '../../../design-system/Badge';
import { Input, Textarea } from '../../../design-system/Input';
import { Modal } from '../../../design-system/Modal';
import {
  FileText,
  Plus,
  Edit3,
  Check,
  Search,
  BookOpen,
  CheckSquare,
  Trash2
} from 'lucide-react';

export const DocsView = () => {
  const { user } = useAuth();
  const { activeProject, tasks = [], setSelectedTask } = useWorkspace();

  const storageKey = `forma_docs_${activeProject?.id || 'default'}`;

  const [docs, setDocs] = useState(() => {
    const saved = localStorage.getItem(storageKey);
    if (saved) {
      try {
        const parsed = JSON.parse(saved);
        if (Array.isArray(parsed) && parsed.length > 0) return parsed;
      } catch (e) {}
    }
    return [
      {
        id: 'doc_1',
        title: 'Project Architecture & Decisions (ADR)',
        category: 'Architecture',
        author: user?.name || 'Workspace Lead',
        updated: 'Today',
        linkedTaskIds: tasks.slice(0, 2).map((t) => t.id),
        content: `## Architecture Decision Records (ADR)\n\nThis project runs on the **Forma Work Operating Environment**.\n\n### Shared Task Pipeline:\n- Tasks are unified across Kanban, Table, Sprints, Timeline, and Calendar.\n- Authoritative persistence backed by Zoho Catalyst Data Store and AppSail Node.js 22 runtime.`
      },
      {
        id: 'doc_2',
        title: 'Neomorphic Interface & Tokens Guide',
        category: 'Specifications',
        author: user?.name || 'Workspace Lead',
        updated: 'Yesterday',
        linkedTaskIds: tasks.slice(0, 1).map((t) => t.id),
        content: `## Neomorphic Design System Tokens\n\nForma utilizes tactical neomorphic dual-shadow modeling.\n\n### Light Palette:\n- Background: #f6fafd\n- Accent: #23beb3\n\n### Dark Palette:\n- Background: #111417\n- Surface: #1B1F23\n- Accent: #43DDD0`
      }
    ];
  });

  const [activeDocId, setActiveDocId] = useState(() => docs[0]?.id || 'doc_1');
  const [search, setSearch] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [isEditing, setIsEditing] = useState(false);
  const [isNewDocModalOpen, setIsNewDocModalOpen] = useState(false);

  // Form State for creating new doc
  const [newTitle, setNewTitle] = useState('');
  const [newCategory, setNewCategory] = useState('Specifications');
  const [newTemplate, setNewTemplate] = useState('spec');

  useEffect(() => {
    localStorage.setItem(storageKey, JSON.stringify(docs));
  }, [docs, storageKey]);

  const activeDoc = docs.find((d) => d.id === activeDocId) || docs[0];
  const [editContent, setEditContent] = useState(activeDoc?.content || '');

  const categories = ['all', 'Architecture', 'Specifications', 'Runbooks', 'Meeting Notes'];

  const filteredDocs = docs.filter((d) => {
    const matchesSearch = d.title.toLowerCase().includes(search.toLowerCase()) || d.content.toLowerCase().includes(search.toLowerCase());
    const matchesCat = selectedCategory === 'all' || d.category.toLowerCase() === selectedCategory.toLowerCase();
    return matchesSearch && matchesCat;
  });

  const handleSelectDoc = (docId) => {
    setActiveDocId(docId);
    setIsEditing(false);
    const target = docs.find((d) => d.id === docId);
    if (target) setEditContent(target.content);
  };

  const handleSaveEdit = () => {
    setDocs((prev) =>
      prev.map((d) => (d.id === activeDocId ? { ...d, content: editContent, updated: 'Just now' } : d))
    );
    setIsEditing(false);
  };

  const handleDeleteDoc = (docId) => {
    setDocs((prev) => prev.filter((d) => d.id !== docId));
    if (activeDocId === docId && docs.length > 1) {
      const nextDoc = docs.find((d) => d.id !== docId);
      if (nextDoc) handleSelectDoc(nextDoc.id);
    }
  };

  const handleCreateDocSubmit = (e) => {
    e?.preventDefault();
    if (!newTitle.trim()) return;

    let defaultBody = '## ' + newTitle + '\n\nStart writing documentation here...';
    if (newTemplate === 'prd') {
      defaultBody = `## Problem Statement\nDescribe the customer pain point.\n\n## Target Audience\nWho will benefit from this feature?\n\n## Success Metrics\n- User adoption rate\n- Latency reduction`;
    } else if (newTemplate === 'spec') {
      defaultBody = `## Architecture Overview\nSystem topology and interactions.\n\n## Data Model & Schemas\nTable definitions and API endpoints.\n\n## Security & Permissions\nRBAC rules and access authorization.`;
    }

    const newDoc = {
      id: 'doc_' + Date.now(),
      title: newTitle.trim(),
      category: newCategory,
      author: user?.name || 'Workspace Owner',
      updated: 'Just now',
      linkedTaskIds: tasks.slice(0, 1).map((t) => t.id),
      content: defaultBody
    };

    setDocs([newDoc, ...docs]);
    setActiveDocId(newDoc.id);
    setEditContent(newDoc.content);
    setNewTitle('');
    setIsNewDocModalOpen(false);
  };

  return (
    <div
      style={{
        display: 'grid',
        gridTemplateColumns: '280px 1fr',
        gap: '20px',
        minHeight: '560px'
      }}
      className="animate-fade-in"
    >
      {/* Doc Tree Sidebar */}
      <div
        style={{
          backgroundColor: 'var(--surface-recessed)',
          boxShadow: 'var(--shadow-recessed-sm)',
          border: '1px solid var(--border-subtle)',
          borderRadius: 'var(--radius-card)',
          padding: '16px',
          display: 'flex',
          flexDirection: 'column',
          gap: '12px'
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '13px', fontWeight: '800' }}>
            <BookOpen size={16} color="var(--accent-teal)" />
            <span>Docs & Wiki</span>
          </div>

          <button
            onClick={() => setIsNewDocModalOpen(true)}
            style={{
              background: 'none',
              border: 'none',
              color: 'var(--accent-teal)',
              cursor: 'pointer',
              padding: '4px',
              display: 'flex',
              alignItems: 'center'
            }}
            title="Create New Doc"
          >
            <Plus size={16} />
          </button>
        </div>

        <Input
          placeholder="Filter wiki pages..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          icon={<Search size={14} />}
          size="sm"
        />

        {/* Category Pills */}
        <div style={{ display: 'flex', gap: '4px', flexWrap: 'wrap' }}>
          {categories.map((c) => (
            <button
              key={c}
              onClick={() => setSelectedCategory(c)}
              style={{
                padding: '3px 8px',
                borderRadius: '999px',
                backgroundColor: selectedCategory === c ? 'var(--surface-card)' : 'transparent',
                border: selectedCategory === c ? '1px solid var(--accent-teal)' : '1px solid transparent',
                color: selectedCategory === c ? 'var(--accent-teal)' : 'var(--text-muted)',
                fontSize: '11px',
                fontWeight: '600',
                cursor: 'pointer'
              }}
            >
              {c}
            </button>
          ))}
        </div>

        <div style={{ height: '1px', backgroundColor: 'var(--border-divider)', margin: '4px 0' }} />

        {/* Document List */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: '4px', overflowY: 'auto' }}>
          {filteredDocs.map((doc) => {
            const isSelected = activeDoc?.id === doc.id;
            return (
              <div
                key={doc.id}
                onClick={() => handleSelectDoc(doc.id)}
                style={{
                  padding: '10px 12px',
                  borderRadius: 'var(--radius-md)',
                  backgroundColor: isSelected ? 'var(--surface-card)' : 'transparent',
                  boxShadow: isSelected ? 'var(--shadow-raised-sm)' : 'none',
                  border: isSelected ? '1px solid var(--border-subtle)' : '1px solid transparent',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'flex-start',
                  justifyContent: 'space-between',
                  gap: '8px',
                  transition: 'all 0.15s'
                }}
              >
                <div style={{ display: 'flex', alignItems: 'flex-start', gap: '8px', flex: 1, minWidth: 0 }}>
                  <FileText size={15} style={{ color: isSelected ? 'var(--accent-teal)' : 'var(--text-muted)', marginTop: '2px', flexShrink: 0 }} />
                  <div style={{ minWidth: 0 }}>
                    <div
                      style={{
                        fontSize: '13px',
                        fontWeight: isSelected ? '700' : '500',
                        color: isSelected ? 'var(--accent-teal)' : 'var(--text-primary)',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap'
                      }}
                    >
                      {doc.title}
                    </div>
                    <div style={{ fontSize: '11px', color: 'var(--text-muted)', marginTop: '2px' }}>
                      {doc.category} • {doc.updated}
                    </div>
                  </div>
                </div>

                {docs.length > 1 && (
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      if (window.confirm(`Delete document "${doc.title}"?`)) {
                        handleDeleteDoc(doc.id);
                      }
                    }}
                    style={{
                      background: 'none',
                      border: 'none',
                      color: 'var(--text-muted)',
                      cursor: 'pointer',
                      padding: '2px',
                      opacity: 0.6
                    }}
                    title="Delete doc"
                  >
                    <Trash2 size={13} />
                  </button>
                )}
              </div>
            );
          })}
        </div>
      </div>

      {/* Main Document Content Area */}
      {activeDoc ? (
        <Card padding="28px" style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
          {/* Header Row */}
          <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', flexWrap: 'wrap', gap: '12px', borderBottom: '1px solid var(--border-divider)', paddingBottom: '16px' }}>
            <div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '6px' }}>
                <Badge variant="teal" size="sm">{activeDoc.category}</Badge>
                <h1 style={{ fontSize: '22px', fontWeight: '800' }}>{activeDoc.title}</h1>
              </div>
              <div style={{ fontSize: '12px', color: 'var(--text-muted)' }}>
                Written by <strong>{activeDoc.author}</strong> • Last updated {activeDoc.updated}
              </div>
            </div>

            <div style={{ display: 'flex', gap: '8px' }}>
              {isEditing ? (
                <Button variant="primary" size="sm" icon={<Check size={15} />} onClick={handleSaveEdit}>
                  Save Changes
                </Button>
              ) : (
                <Button variant="secondary" size="sm" icon={<Edit3 size={15} />} onClick={() => setIsEditing(true)}>
                  Edit Doc
                </Button>
              )}
            </div>
          </div>

          {/* Linked Tasks Mention Bar */}
          {activeDoc.linkedTaskIds && activeDoc.linkedTaskIds.length > 0 && (
            <div
              style={{
                padding: '10px 14px',
                backgroundColor: 'var(--surface-recessed)',
                borderRadius: 'var(--radius-md)',
                display: 'flex',
                alignItems: 'center',
                gap: '10px',
                fontSize: '12px'
              }}
            >
              <span style={{ fontWeight: '700', color: 'var(--text-muted)', display: 'flex', alignItems: 'center', gap: '4px' }}>
                <CheckSquare size={14} color="var(--accent-teal)" /> Referenced Tasks:
              </span>
              <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap' }}>
                {activeDoc.linkedTaskIds.map((tid) => {
                  const tObj = tasks.find((t) => t.id === tid);
                  return (
                    <span
                      key={tid}
                      onClick={() => tObj && setSelectedTask(tObj)}
                      style={{
                        cursor: 'pointer',
                        padding: '2px 8px',
                        borderRadius: '4px',
                        backgroundColor: 'var(--surface-card)',
                        boxShadow: 'var(--shadow-raised-sm)',
                        color: 'var(--accent-teal)',
                        fontWeight: '700',
                        fontSize: '11px'
                      }}
                    >
                      {tid}: {tObj?.title || 'Spec Issue'}
                    </span>
                  );
                })}
              </div>
            </div>
          )}

          {/* Doc Body / Editor */}
          {isEditing ? (
            <Textarea
              value={editContent}
              onChange={(e) => setEditContent(e.target.value)}
              rows={16}
              style={{ fontFamily: 'monospace', fontSize: '13px', lineHeight: '1.6' }}
            />
          ) : (
            <div
              style={{
                fontSize: '14px',
                lineHeight: '1.7',
                color: 'var(--text-primary)',
                whiteSpace: 'pre-wrap'
              }}
            >
              {activeDoc.content}
            </div>
          )}
        </Card>
      ) : (
        <Card padding="48px">
          <div style={{ textAlign: 'center', color: 'var(--text-muted)' }}>
            No document selected. Choose or create a wiki page to start writing.
          </div>
        </Card>
      )}

      {/* CREATE NEW DOC MODAL */}
      <Modal
        isOpen={isNewDocModalOpen}
        onClose={() => setIsNewDocModalOpen(false)}
        title="Create Documentation Page"
        subtitle="Author engineering specifications, runbooks, or architectural decisions"
        footer={
          <>
            <Button variant="ghost" onClick={() => setIsNewDocModalOpen(false)}>
              Cancel
            </Button>
            <Button variant="primary" onClick={handleCreateDocSubmit} icon={<Plus size={15} />}>
              Create Doc
            </Button>
          </>
        }
      >
        <form onSubmit={handleCreateDocSubmit} style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
          <Input
            label="Document Title *"
            placeholder="e.g., GraphQL Federation Architecture, Onboarding Runbook"
            value={newTitle}
            onChange={(e) => setNewTitle(e.target.value)}
            required
            autoFocus
          />

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
            <Input
              label="Category"
              placeholder="e.g. Architecture, Runbooks"
              value={newCategory}
              onChange={(e) => setNewCategory(e.target.value)}
            />

            <div>
              <div style={{ fontSize: '13px', fontWeight: '700', marginBottom: '6px', color: 'var(--text-primary)' }}>
                Template
              </div>
              <select
                value={newTemplate}
                onChange={(e) => setNewTemplate(e.target.value)}
                style={{
                  width: '100%',
                  padding: '9px 12px',
                  borderRadius: 'var(--radius-md)',
                  backgroundColor: 'var(--surface-recessed)',
                  border: '1px solid var(--border-subtle)',
                  color: 'var(--text-primary)',
                  fontSize: '13px'
                }}
              >
                <option value="spec">Technical Specification</option>
                <option value="prd">Product Requirement Doc (PRD)</option>
                <option value="blank">Blank Document</option>
              </select>
            </div>
          </div>
        </form>
      </Modal>
    </div>
  );
};
